export const HAS_SPOUSE_STORAGE_KEY = 'hasSpouse';

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const unwrapPayload = (value: unknown): Record<string, unknown> | null => {
    if (!isRecord(value)) {
        return null;
    }
    if (
        isRecord(value.data) &&
        !('has_spouse' in value) &&
        !('hasSpouse' in value) &&
        !('spouse_user_id' in value) &&
        !('spouseUserId' in value)
    ) {
        return value.data;
    }
    return value;
};

const coerceHasSpouse = (value: unknown): boolean | undefined => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (value === 1 || value === '1' || value === 'true' || value === 'Yes' || value === 'yes') {
        return true;
    }
    if (value === 0 || value === '0' || value === 'false' || value === 'No' || value === 'no') {
        return false;
    }
    return undefined;
};

/** Read `has_spouse` from an Additional Details GET/POST body. */
export const parseHasSpouseFromPayload = (value: unknown): boolean | undefined => {
    const payload = unwrapPayload(value);
    if (!payload) {
        return undefined;
    }
    if ('has_spouse' in payload) {
        return coerceHasSpouse(payload.has_spouse);
    }
    if ('hasSpouse' in payload) {
        return coerceHasSpouse(payload.hasSpouse);
    }
    return undefined;
};

export const persistHasSpouseDraft = (hasSpouse: boolean): void => {
    const value = hasSpouse ? 'Yes' : 'No';
    window.localStorage.setItem(HAS_SPOUSE_STORAGE_KEY, JSON.stringify(value));
    window.dispatchEvent(new Event('storage'));
};

export const SPOUSE_USER_ID_STORAGE_KEY = 'spouseUserId';

const asUserId = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return value;
    }
    if (typeof value === 'string' && value.trim()) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return null;
};

/** JWT `spouse_id` hint after a token refresh. Not authorization — ended relations still 403. */
export const readJwtSpouseId = (token?: string | null): number | null => {
    if (!token) {
        return null;
    }
    try {
        const segment = token.split('.')[1];
        if (!segment) {
            return null;
        }
        const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(window.atob(normalized)) as { spouse_id?: unknown };
        return asUserId(payload.spouse_id);
    } catch {
        return null;
    }
};

/** Read `spouse_user_id` from an Additional Details GET/POST body. */
export const parseSpouseUserIdFromPayload = (value: unknown): number | null => {
    const payload = unwrapPayload(value);
    if (!payload) {
        return null;
    }
    return (
        asUserId(payload.spouse_user_id) ??
        asUserId(payload.spouseUserId) ??
        asUserId(payload.related_user_id)
    );
};

export const persistSpouseUserId = (spouseUserId: number | null): void => {
    if (spouseUserId && spouseUserId > 0) {
        window.localStorage.setItem(SPOUSE_USER_ID_STORAGE_KEY, String(spouseUserId));
    } else {
        window.localStorage.removeItem(SPOUSE_USER_ID_STORAGE_KEY);
    }
    window.dispatchEvent(new Event('storage'));
};

export const readCachedSpouseUserId = (): number | null => {
    try {
        return asUserId(window.localStorage.getItem(SPOUSE_USER_ID_STORAGE_KEY));
    } catch {
        return null;
    }
};

export const persistSpouseLifecycle = (
    hasSpouse: boolean | undefined,
    spouseUserId: number | null
): void => {
    if (hasSpouse !== undefined) {
        persistHasSpouseDraft(hasSpouse);
    }
    persistSpouseUserId(hasSpouse === false ? null : spouseUserId);
};

export const omitHasSpouse = <T extends object>(payload: T): T => {
    if (!isRecord(payload)) {
        return payload;
    }
    const {
        has_spouse: _hasSpouseSnake,
        hasSpouse: _hasSpouseCamel,
        spouse_user_id: _spouseUserIdSnake,
        spouseUserId: _spouseUserIdCamel,
        ...rest
    } = payload;
    return rest as T;
};
